import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  LocalOffer as CouponIcon,
} from "@mui/icons-material";
import { useSnack } from "../provider/SnackbarProvider";
import marketingApi from "../services/marketingApi";

interface MarketingPopupProps {
  open: boolean;
  onClose: () => void;
}

interface MarketingData {
  _id: string;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  discountPercentage: number;
  couponId: string;
  isActive: boolean;
}

const MarketingPopup: React.FC<MarketingPopupProps> = ({ open, onClose }) => {
  const [marketingData, setMarketingData] = useState<MarketingData | null>(
    null
  );
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phone: "",
    acceptMarketing: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [generatedCoupon, setGeneratedCoupon] = useState<string>("");
  const [showCoupon, setShowCoupon] = useState(false);
  const showSnackbar = useSnack();

  const fetchMarketingData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await marketingApi.getMarketingData();
      setMarketingData(data);
    } catch (error) {
      // No active marketing campaign - don't show popup
      setMarketingData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchMarketingData();
    }
  }, [fetchMarketingData, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email || !formData.acceptMarketing) {
      showSnackbar("error", "אנא מלא את כל השדות הנדרשים");
      return;
    }

    try {
      setSubmitting(true);
      const response = await marketingApi.submitMarketingData({
        ...formData,
        marketingDataId: marketingData?._id || "",
      });

      if (response.couponCode) {
        setGeneratedCoupon(response.couponCode);
        setShowCoupon(true);
        showSnackbar("success", "הקופון נוצר בהצלחה!");
      }
    } catch (error: any) {
      console.error("Error submitting marketing data:", error);
      showSnackbar(
        "error",
        error.response?.data?.message || "שגיאה בשליחת הנתונים"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      email: "",
      phone: "",
      acceptMarketing: false,
    });
    setGeneratedCoupon("");
    setShowCoupon(false);
    onClose();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Don't render anything if no marketing data
  if (!marketingData) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative" }}>
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            bgcolor: "rgba(255,255,255,0.9)",
            "&:hover": { bgcolor: "rgba(255,255,255,1)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Background Image */}
        {marketingData.image && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${marketingData.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.1,
              zIndex: 0,
            }}
          />
        )}

        <Box sx={{ position: "relative", zIndex: 1, p: 4 }}>
          {showCoupon ? (
            // Coupon Display
            <Box textAlign="center">
              <CouponIcon sx={{ fontSize: 60, color: "#047ABC", mb: 2 }} />
              <Typography variant="h4" fontWeight="bold" color="#047ABC" mb={2}>
                מזל טוב! קיבלת קופון
              </Typography>
              <Chip
                label={generatedCoupon}
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  bgcolor: "#047ABC",
                  color: "white",
                  p: 2,
                  mb: 3,
                }}
              />
              <Typography variant="body1" color="text.secondary" mb={3}>
                הקופון נשלח לכתובת האימייל שלך
              </Typography>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  bgcolor: "#047ABC",
                  "&:hover": { bgcolor: "#035a8c" },
                }}
              >
                סגור
              </Button>
            </Box>
          ) : (
            // Marketing Form
            <>
              <Box sx={{ textAlign: "center", pb: 1 }}>
                <Typography variant="h4" fontWeight="bold" color="#222">
                  {marketingData.title}
                </Typography>
              </Box>

              <Typography variant="h6" textAlign="center" color="#666" mb={1}>
                {marketingData.subtitle}
              </Typography>

              <Typography
                variant="body2"
                textAlign="center"
                color="#888"
                mb={4}
              >
                {marketingData.description}
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="שם פרטי (אופציונלי)"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  sx={{ mb: 2 }}
                  dir="rtl"
                />

                <TextField
                  fullWidth
                  label="דואר אלקטרוני *"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  sx={{ mb: 2 }}
                  dir="rtl"
                />

                <TextField
                  fullWidth
                  label="טלפון נייד"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  sx={{ mb: 3 }}
                  dir="rtl"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acceptMarketing}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          acceptMarketing: e.target.checked,
                        })
                      }
                      required
                    />
                  }
                  label="מאשר/ת קבלת דיוור ו/או מידע פרסומי באימייל ו/או במסרונים"
                  sx={{ mb: 3, alignItems: "flex-start" }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  sx={{
                    bgcolor: "#047ABC",
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    "&:hover": { bgcolor: "#035a8c" },
                  }}
                >
                  {submitting ? <CircularProgress size={24} /> : "הרשמה"}
                </Button>

                <Typography
                  variant="caption"
                  color="#888"
                  textAlign="center"
                  display="block"
                  mt={2}
                >
                  *בכפוף לתקנון האתר | לנרשמים חדשים בלבד | יחושב מיתרת העסקה
                  לאחר הפחתת כלל ההנחות | לשימוש חד פעמי
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MarketingPopup;
